import type { ExportOptions } from '@/components/explorer/ExportModal'

// Type for database row data
type DatabaseRow = Record<string, unknown>

export function downloadData(data: DatabaseRow[], options: ExportOptions, filename: string) {
  let content: string
  let mimeType: string
  let fileExtension: string

  switch (options.format) {
    case 'csv':
      content = generateCSV(data, options)
      mimeType = 'text/csv'
      fileExtension = 'csv'
      break
    case 'json':
      content = generateJSON(data, options)
      mimeType = 'application/json'
      fileExtension = 'json'
      break
    case 'sql':
      content = generateSQL(data, options, filename)
      mimeType = 'text/plain'
      fileExtension = 'sql'
      break
    default:
      throw new Error(`Unsupported export format: ${String(options.format)}`)
  }

  // Create and download file
  const blob = new Blob([content], { type: mimeType })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${filename}.${fileExtension}`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

function generateCSV(data: DatabaseRow[], options: ExportOptions): string {
  const lines: string[] = []

  // Add headers if requested
  if (options.includeHeaders) {
    lines.push(options.columns.map(col => escapeCSVField(col)).join(','))
  }

  // Add data rows
  for (const row of data) {
    const values = options.columns.map(col => {
      const value = row[col]
      return escapeCSVField(formatCellValue(value))
    })
    lines.push(values.join(','))
  }

  return lines.join('\n')
}

function generateJSON(data: DatabaseRow[], options: ExportOptions): string {
  const filteredData = data.map(row => {
    const filteredRow: DatabaseRow = {}
    for (const col of options.columns) {
      filteredRow[col] = row[col]
    }
    return filteredRow
  })

  return JSON.stringify(filteredData, null, 2)
}

function generateSQL(data: DatabaseRow[], options: ExportOptions, tableName: string): string {
  const lines: string[] = []
  
  // Add header comment
  lines.push(`-- Generated SQL INSERT statements for ${tableName}`)
  lines.push(`-- Generated on ${new Date().toISOString()}`)
  lines.push('')
  
  // Generate INSERT statements
  for (const row of data) {
    const columns = options.columns.join(', ')
    const values = options.columns.map(col => {
      const value = row[col]
      return formatSQLValue(value)
    }).join(', ')
    
    lines.push(`INSERT INTO ${tableName} (${columns}) VALUES (${values});`)
  }
  
  return lines.join('\n')
}

function escapeCSVField(field: string): string {
  const stringField = typeof field === 'string' ? field : String(field)
  
  // If field contains comma, newline, or quote, wrap in quotes and escape internal quotes
  if (stringField.includes(',') || stringField.includes('\n') || stringField.includes('"')) {
    return `"${stringField.replace(/"/g, '""')}"`
  }
  
  return stringField
}

function formatSQLValue(value: unknown): string {
  if (value === null || value === undefined) {
    return 'NULL'
  }
  
  if (typeof value === 'boolean') {
    return value ? 'TRUE' : 'FALSE'
  }
  
  if (typeof value === 'number') {
    return String(value)
  }
  
  // Escape single quotes and wrap in quotes
  if (typeof value === 'string') {
    return `'${value.replace(/'/g, "''")}'`
  }
  if (value instanceof Date) {
    return `'${value.toISOString().replace(/'/g, "''")}'`
  }
  if (typeof value === 'object') {
    try {
      return `'${JSON.stringify(value).replace(/'/g, "''")}'`
    } catch {
      return `'[object]'`
    }
  }
  const str = typeof value === 'string' ? value : JSON.stringify(value)
  return `'${str.replace(/'/g, "''")}'`
}

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) {
    return 'NULL'
  }
  
  if (typeof value === 'boolean') {
    return value ? 'TRUE' : 'FALSE'
  }
  
  if (typeof value === 'string') {
    return value
  }
  if (value instanceof Date) {
    return value.toISOString()
  }
  if (typeof value === 'object' && value !== null) {
    try {
      return JSON.stringify(value)
    } catch {
      return '[object]'
    }
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  try {
    return JSON.stringify(value)
  } catch {
    return '[object]'
  }
}
